import { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../ui/table';
import { Button } from '../../ui/button';
import { Edit, Trash2, Eye, Trophy } from 'lucide-react';
import { useToast } from '../../ui/use-toast';
import { supabase } from '../../../lib/supabase';
import { useContestantFilters } from '../../../stores/contestantFilters';
import { EditContestantDialog } from './EditContestantDialog';
import { Pagination } from '../../ui/pagination';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../../ui/dialog';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

interface Contestant {
  id: string;
  contest_id: string;
  name: string;
  photo_url: string;
  unique_code: string;
  description: string;
  created_at: string;
  votes_count?: number;
  contests?: {
    name: string;
    slug: string;
    status: string;
    end_date: string;
  };
}

export const ContestantTable = () => {
  const [contestants, setContestants] = useState<Contestant[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();
  const { search, contestId, sortBy } = useContestantFilters();
  const [selectedContestant, setSelectedContestant] = useState<Contestant | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [contestantToDelete, setContestantToDelete] = useState<Contestant | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const itemsPerPageOptions = [50, 100, 200, 500];
  const [totalContestants, setTotalContestants] = useState(0);

  const fetchContestants = async () => {
    try {
      // Fetch total count first
      let countQuery = supabase
        .from('contestants')
        .select('*', { count: 'exact', head: true });

      if (contestId) {
        countQuery = countQuery.eq('contest_id', contestId);
      }

      if (search) {
        countQuery = countQuery.or(`name.ilike.%${search}%,unique_code.ilike.%${search}%`);
      }

      const { count } = await countQuery;
      setTotalContestants(count || 0);

      // Then fetch paginated data
      let query = supabase
        .from('contestants')
        .select(`
          *,
          contests (
            name,
            slug,
            status,
            end_date
          )
        `)
        .range((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage - 1);

      if (contestId) {
        query = query.eq('contest_id', contestId);
      }

      if (search) {
        query = query.or(`name.ilike.%${search}%,unique_code.ilike.%${search}%`);
      }

      switch (sortBy) {
        case 'name':
          query = query.order('name');
          break;
        case 'votes':
          // We'll sort by votes after fetching
          break;
        case 'created_at':
          query = query.order('created_at', { ascending: false });
          break;
        default:
          query = query.order('created_at', { ascending: false });
      }

      const { data, error } = await query;

      if (error) throw error;

      // Filtrer côté client pour exclure les candidats dont le concours est terminé
      const now = new Date();
      const filtered = (data || []).filter((contestant: any) => {
        const contest = contestant.contests;
        if (!contest) return false;
        if (contest.status === 'completed') return false;
        if (contest.end_date && new Date(contest.end_date) < now) return false;
        return true;
      });

      // Pour chaque candidat, compter les votes
      const contestantsWithVotes = await Promise.all(
        filtered.map(async (contestant: any) => {
          const { count } = await supabase
            .from('votes')
            .select('id', { count: 'exact', head: true })
            .eq('contestant_id', contestant.id);

          return {
            ...contestant,
            votes_count: count || 0,
          };
        })
      );

      if (sortBy === 'votes') {
        contestantsWithVotes.sort((a, b) => (b.votes_count || 0) - (a.votes_count || 0));
      }

      setContestants(contestantsWithVotes);
    } catch (error: any) {
      toast({
        variant: 'destructive',
        title: 'Erreur',
        description: error.message || 'Impossible de charger les participants'
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setCurrentPage(1); // Reset to first page when filters change
  }, [search, contestId, sortBy]);

  useEffect(() => {
    fetchContestants();
  }, [currentPage, search, contestId, sortBy, itemsPerPage]);

  if (loading) {
    return <div className="text-center p-4">Chargement...</div>;
  }

  const viewPublicLink = (uniqueCode: string) => `/${uniqueCode}`;

  return (
    <div className="w-full">
      <Table>
        <TableHeader>
          <TableRow className="border-b border-white/10 hover:bg-white/5">
            <TableHead className="text-gray-400 font-medium">Photo</TableHead>
            <TableHead className="text-gray-400 font-medium">Nom</TableHead>
            <TableHead className="text-gray-400 font-medium">Code</TableHead>
            <TableHead className="text-gray-400 font-medium">Concours</TableHead>
            <TableHead className="text-gray-400 font-medium text-right">Votes</TableHead>
            <TableHead className="text-gray-400 font-medium">Date d'ajout</TableHead>
            <TableHead className="text-gray-400 font-medium">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {contestants.map((contestant) => (
            <TableRow 
              key={contestant.id}
              className="border-b border-white/10 hover:bg-white/5 transition-colors"
            >
              <TableCell>
                <div className="relative h-10 w-10">
                  {contestant.photo_url ? (
                    <img
                      src={contestant.photo_url}
                      alt={contestant.name}
                      className="h-10 w-10 rounded-lg object-cover"
                    />
                  ) : (
                    <div className="h-10 w-10 rounded-lg bg-white/5 flex items-center justify-center">
                      <Trophy className="h-5 w-5 text-gray-400" />
                    </div>
                  )}
                </div>
              </TableCell>
              <TableCell className="font-medium text-white">
                <Link 
                  to={`/${contestant.unique_code}`}
                  className="text-white hover:text-yellow-400 transition-colors"
                >
                  {contestant.name}
                </Link>
              </TableCell>
              <TableCell className="text-gray-400">
                {contestant.unique_code}
              </TableCell>
              <TableCell className="text-gray-300">
                <Link 
                  to={`/c/${contestant.contests?.slug}`}
                  className="text-white hover:text-yellow-400 transition-colors"
                >
                  {contestant.contests?.name}
                </Link>
              </TableCell>
              <TableCell className="text-right">
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-400/10 text-yellow-400">
                  {contestant.votes_count || 0}
                </span>
              </TableCell>
              <TableCell className="text-gray-300">
                {format(new Date(contestant.created_at), 'dd MMM yyyy', { locale: fr })}
              </TableCell>
              <TableCell>
                <div className="flex items-center gap-2">
                  <Link
                    to={viewPublicLink(contestant.unique_code)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="h-8 w-8 rounded-lg bg-white/5 hover:bg-white/10 transition-colors flex items-center justify-center text-gray-400 hover:text-white"
                  >
                    <Eye className="h-4 w-4" />
                  </Link>
                  <button
                    onClick={() => {
                      setSelectedContestant(contestant);
                      setEditDialogOpen(true);
                    }}
                    className="h-8 w-8 rounded-lg bg-white/5 hover:bg-white/10 transition-colors flex items-center justify-center text-gray-400 hover:text-white"
                  >
                    <Edit className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => {
                      setContestantToDelete(contestant);
                      setDeleteDialogOpen(true);
                    }}
                    className="h-8 w-8 rounded-lg bg-white/5 hover:bg-white/10 transition-colors flex items-center justify-center text-gray-400 hover:text-white"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="bg-gray-900 text-white border border-white/10">
          <DialogHeader>
            <DialogTitle>Confirmer la suppression</DialogTitle>
            <DialogDescription className="text-gray-400">
              Êtes-vous sûr de vouloir supprimer le candidat "{contestantToDelete?.name}" ? Cette action est irréversible.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="sm:justify-start">
            <div className="flex gap-2">
              <Button
                variant="destructive"
                onClick={async () => {
                  if (contestantToDelete) {
                    const { error } = await supabase
                      .from('contestants')
                      .delete()
                      .eq('id', contestantToDelete.id);

                    if (error) {
                      toast({
                        title: "Erreur",
                        description: "Une erreur est survenue lors de la suppression du candidat.",
                        variant: "destructive",
                      });
                    } else {
                      toast({
                        title: "Succès",
                        description: "Le candidat a été supprimé avec succès.",
                      });
                      setContestants(contestants.filter(c => c.id !== contestantToDelete.id));
                    }
                  }
                  setDeleteDialogOpen(false);
                }}
              >
                Supprimer
              </Button>
              <Button
                variant="outline"
                onClick={() => setDeleteDialogOpen(false)}
              >
                Annuler
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {selectedContestant && (
        <EditContestantDialog
          open={editDialogOpen}
          onOpenChange={setEditDialogOpen}
          contestant={selectedContestant}
          onSave={(updatedContestant) => {
            setContestants(
              contestants.map((c) =>
                c.id === updatedContestant.id ? updatedContestant : c
              )
            );
          }}
        />
      )}

      {/* Pagination */}
      <div className="flex items-center justify-between mt-6">
        <Pagination
          page={currentPage}
          pageSize={itemsPerPage}
          total={totalContestants}
          onPageChange={setCurrentPage}
        />
        <div className="flex items-center gap-2">
          <span className="text-gray-400">Par page :</span>
          <select
            className="bg-gray-900 border border-white/10 rounded px-2 py-1 text-white"
            value={itemsPerPage}
            onChange={e => {
              setItemsPerPage(Number(e.target.value));
              setCurrentPage(1);
            }}
          >
            {itemsPerPageOptions.map(opt => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );
};